import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

const appendIfPresent = (params, key, value) => {
  if (value === undefined || value === null) return;
  const normalized = String(value).trim();
  if (normalized) params.append(key, normalized);
};

const buildQueryString = (filters = {}) => {
  const params = new URLSearchParams();
  appendIfPresent(params, 'fromDate', filters.fromDate);
  appendIfPresent(params, 'toDate', filters.toDate);
  appendIfPresent(params, 'status', filters.status);
  appendIfPresent(params, 'tripType', filters.tripType);
  appendIfPresent(params, 'providerUserReferenceId', filters.providerUserReferenceId);
  appendIfPresent(params, 'sourceType', filters.sourceType);
  appendIfPresent(params, 'sourceReferenceId', filters.sourceReferenceId);
  return params.toString();
};

const parseFilenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] || null;
};

const readErrorMessage = async (response, fallback) => {
  const text = await response.text();
  if (!text) return fallback;

  try {
    const parsed = JSON.parse(text);
    return parsed?.error || parsed?.message || text || fallback;
  } catch {
    return text || fallback;
  }
};

const fetchMileageFile = async (filters = {}, format = 'csv') => {
  const query = buildQueryString(filters);
  const finalQuery = `${query ? `${query}&` : ''}format=${encodeURIComponent(format)}`;
  const res = await fetch(`${config.api.url}/mileage/trips/export?${finalQuery}`, {
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    const errorMessage = await readErrorMessage(res, `HTTP error! status: ${res.status}`);
    const error = new Error(errorMessage);
    error.status = res.status;
    error.statusText = res.statusText;
    throw error;
  }

  const blob = await res.blob();
  return {
    blob,
    filename: parseFilenameFromDisposition(res.headers.get('content-disposition')),
  };
};

const mileageApi = {
  getSourcePreview: (filters = {}) => {
    const query = buildQueryString(filters);
    return fetch(`${config.api.url}/mileage/source-preview${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse);
  },

  getTrips: (filters = {}) => {
    const query = buildQueryString(filters);
    return fetch(`${config.api.url}/mileage/trips${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse);
  },

  createTrip: (request) => fetch(`${config.api.url}/mileage/trips`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  }).then(handleResponse),

  updateTrip: (referenceId, request) => fetch(`${config.api.url}/mileage/trips/${referenceId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  }).then(handleResponse),

  bulkApprove: (referenceIds) => fetch(`${config.api.url}/mileage/trips/bulk-approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ referenceIds }),
  }).then(handleResponse),

  exportTrips: fetchMileageFile,
};

export default mileageApi;
